import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Pill, AlertTriangle, CheckCircle2, Clock, Plus, Shield, Download,
  ChevronDown, Activity, FileText, RefreshCw, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { given: 'bg-[#A8E6CF]/20 border-[#A8E6CF]/40 text-[#A8E6CF]', refused: 'bg-red-500/10 border-red-500/30 text-red-400', withheld: 'bg-amber-500/10 border-amber-500/30 text-amber-400', omitted: 'bg-muted border-border text-muted-foreground', error: 'bg-red-500/20 border-red-500/40 text-red-500', late: 'bg-amber-500/10 border-amber-500/30 text-amber-400', early: 'bg-blue-500/10 border-blue-500/30 text-blue-400', pending: 'bg-muted/50 border-border text-muted-foreground' };
const SLOT_COLORS = { given: 'bg-[#4CAF50]', refused: 'bg-[#F44336]', withheld: 'bg-[#FF9800]', pending: 'bg-muted', overdue: 'bg-[#F44336]/60', due: 'bg-[#00A6A0]', upcoming: 'bg-muted/30' };

export default function EmarPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const [meds, setMeds] = useState([]);
  const [records, setRecords] = useState([]);
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState('chart');
  const [adminDialog, setAdminDialog] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [adminForm, setAdminForm] = useState({ status: 'given', scheduled_time: '', actual_time: '', notes: '', reason_not_given: '', allergy_override_reason: '', witnessed_by_name: '', prn_reason: '', patch_site: '', insulin_units: '', blood_glucose_before: '', duplicate_override: false });
  const [safetyAlerts, setSafetyAlerts] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [reconDialog, setReconDialog] = useState(false);
  const [reconForm, setReconForm] = useState({ source: 'hospital_discharge', hospital_name: '', discharge_date: '', changes: '', notes: '' });
  const [reports, setReports] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const canAdmin = ['agency_admin', 'ceo', 'caregiver'].includes(user?.role);

  useEffect(() => { if (clientId) { fetchMeds(); fetchRecords(); fetchClient(); } }, [clientId, date]);

  const fetchMeds = async () => { try { const { data } = await axios.get(`${API}/api/clinical-compliance/mar/medications/${clientId}`, { withCredentials: true }); setMeds(Array.isArray(data) ? data : data.medications || []); } catch { /* ignore */ } };
  const fetchRecords = async () => { try { const { data } = await axios.get(`${API}/api/clinical-compliance/mar/records/${clientId}?date=${date}`, { withCredentials: true }); setRecords(data); } catch { /* ignore */ } };
  const fetchClient = async () => { try { const { data } = await axios.get(`${API}/api/clinical/my-clients`, { withCredentials: true }); const c = data.find(cl => cl.id === clientId); setClient(c); } catch { /* ignore */ } };
  const fetchRecon = async () => { try { const { data } = await axios.get(`${API}/api/clinical-compliance/mar/reconciliations/${clientId}`, { withCredentials: true }); setReconciliations(data); } catch { /* ignore */ } };
  const fetchReports = async () => { try { const { data } = await axios.get(`${API}/api/clinical-compliance/mar/reports?client_id=${clientId}`, { withCredentials: true }); setReports(data); } catch { /* ignore */ } };

  const openAdminDialog = (med, time) => {
    setSelectedMed(med);
    setSafetyAlerts([]);
    setAdminForm({ status: 'given', scheduled_time: time || '', actual_time: new Date().toTimeString().slice(0, 5), notes: '', reason_not_given: '', allergy_override_reason: '', witnessed_by_name: '', prn_reason: '', patch_site: '', insulin_units: '', blood_glucose_before: '', duplicate_override: false });
    setAdminDialog(true);
  };

  const submitAdmin = async () => {
    if (!selectedMed) return;
    try {
      const payload = { medication_id: selectedMed.id, client_id: clientId, ...adminForm, date };
      if (adminForm.insulin_units) payload.insulin_units = parseFloat(adminForm.insulin_units);
      if (adminForm.blood_glucose_before) payload.blood_glucose_before = parseFloat(adminForm.blood_glucose_before);
      const { data } = await axios.post(`${API}/api/clinical-compliance/mar/administer`, payload, { withCredentials: true });
      if (data.safety_alerts?.length > 0) setSafetyAlerts(data.safety_alerts);
      toast.success(`${selectedMed.medication_name}: ${adminForm.status}`);
      setAdminDialog(false); fetchRecords();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed';
      if (detail.includes('Duplicate')) { setSafetyAlerts([{ type: 'duplicate', severity: 'high', message: detail }]); }
      else if (detail.includes('Allergy')) { setSafetyAlerts([{ type: 'allergy', severity: 'critical', message: detail }]); }
      else { toast.error(detail); }
    }
  };

  const saveRecon = async () => {
    try {
      const changes = reconForm.changes ? reconForm.changes.split('\n').filter(Boolean).map(c => ({ details: c, change_type: 'modified' })) : [];
      await axios.post(`${API}/api/clinical-compliance/mar/reconciliation`, { client_id: clientId, ...reconForm, changes }, { withCredentials: true });
      toast.success('Reconciliation saved'); setReconDialog(false); fetchRecon();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadOffline = async () => {
    try {
      const resp = await axios.get(`${API}/api/clinical-compliance/mar/offline-sheet/${clientId}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = `Offline_MAR_${client?.name || 'sheet'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Offline MAR downloaded');
    } catch { toast.error('Download failed'); }
  };

  const activeMeds = meds.filter(m => m.status === 'active').map(m => ({ ...m, id: m.id || m._id, medication_name: m.medication_name || m.name || 'Unknown' }));
  const scheduledMeds = activeMeds.filter(m => !m.is_prn && (m.times || []).length > 0);
  const prnMeds = activeMeds.filter(m => m.is_prn);
  const timeSlots = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'];

  const getSlotStatus = (med, time) => {
    const r = records.find(rec => rec.medication_id === med.id && rec.scheduled_time === time);
    if (r) return r.status;
    const now = new Date().toTimeString().slice(0, 5);
    if (time <= now) {
      const window = med.administration_window_minutes || 60;
      const tMins = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
      const nowMins = parseInt(now.split(':')[0]) * 60 + parseInt(now.split(':')[1]);
      return nowMins - tMins > window ? 'overdue' : 'due';
    }
    return 'upcoming';
  };

  if (!client) return <DashboardLayout><div className="flex justify-center py-20"><div className="spinner" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="emar-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2"><Pill className="w-5 h-5 text-[#00A6A0]" />eMAR: {client.name}</h1>
            <p className="text-xs text-muted-foreground">{activeMeds.length} active medications | {records.length} administrations today</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-muted border-border text-foreground w-36 text-xs" data-testid="emar-date" />
            <Button size="sm" variant="outline" className="border-border text-xs" onClick={downloadOffline} data-testid="download-offline-btn"><Download className="w-3 h-3 mr-1" />Offline MAR</Button>
          </div>
        </div>

        <div className="flex gap-1 p-1 bg-muted rounded-lg overflow-x-auto">
          {[{ id: 'chart', label: 'MAR Chart' }, { id: 'prn', label: `PRN (${prnMeds.length})` }, { id: 'recon', label: 'Reconciliation' }, { id: 'reports', label: 'Reports' }].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'recon') fetchRecon(); if (t.id === 'reports') fetchReports(); }}
              className={`whitespace-nowrap flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid={`emar-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>

        {/* MAR CHART */}
        {tab === 'chart' && (
          <div className="overflow-x-auto" data-testid="mar-chart">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-2 border border-border text-muted-foreground font-medium sticky left-0 bg-muted/50 min-w-[160px]">Medication</th>
                  {timeSlots.map(t => <th key={t} className="p-2 border border-border text-muted-foreground font-medium w-16 text-center">{t}</th>)}
                </tr>
              </thead>
              <tbody>
                {scheduledMeds.map(med => (
                  <tr key={med.id} className="hover:bg-muted/20">
                    <td className="p-2 border border-border sticky left-0 bg-background">
                      <div className="font-medium text-foreground">{med.medication_name}</div>
                      <div className="text-[10px] text-muted-foreground">{med.dose} {med.route} {med.frequency}
                        {med.is_controlled && <span className="ml-1 px-1 rounded bg-red-500/10 text-red-400">CD</span>}
                        {med.is_insulin && <span className="ml-1 px-1 rounded bg-blue-500/10 text-blue-400">INS</span>}
                        {med.is_patch && <span className="ml-1 px-1 rounded bg-purple-500/10 text-purple-400">PATCH</span>}
                      </div>
                    </td>
                    {timeSlots.map(t => {
                      const hasMedTime = (med.times || []).includes(t);
                      if (!hasMedTime) return <td key={t} className="p-1 border border-border bg-muted/10 text-center">-</td>;
                      const status = getSlotStatus(med, t);
                      return (
                        <td key={t} className="p-1 border border-border text-center">
                          <button onClick={() => canAdmin && status !== 'given' && openAdminDialog(med, t)}
                            className={`w-full py-1.5 rounded text-[10px] font-bold transition-colors ${SLOT_COLORS[status] || 'bg-muted'} ${status === 'given' ? 'text-white cursor-default' : status === 'refused' ? 'text-white cursor-default' : 'text-foreground cursor-pointer hover:opacity-80'}`}
                            disabled={!canAdmin || status === 'given'} data-testid={`slot-${med.id}-${t}`}>
                            {status === 'given' ? '\u2713' : status === 'refused' ? 'R' : status === 'withheld' ? 'W' : status === 'overdue' ? '!' : status === 'due' ? '\u25CB' : '\u00B7'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {scheduledMeds.length === 0 && <tr><td colSpan={timeSlots.length + 1} className="p-4 text-center text-muted-foreground">No scheduled medications</td></tr>}
              </tbody>
            </table>
            <div className="flex gap-3 mt-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#4CAF50]" />Given</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#00A6A0]" />Due</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F44336]/60" />Overdue</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F44336]" />Refused</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#FF9800]" />Withheld</span>
            </div>
          </div>
        )}

        {/* PRN TAB */}
        {tab === 'prn' && (
          <div className="space-y-3" data-testid="prn-tab">
            {prnMeds.length === 0 ? <p className="text-center text-muted-foreground py-8">No PRN medications</p> : (
              prnMeds.map(med => {
                const givenToday = records.filter(r => r.medication_id === med.id && r.status === 'given').length;
                return (
                  <Card key={med.id} className="glass-card border-border" data-testid={`prn-card-${med.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground text-sm">{med.medication_name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400">PRN</span>
                            {med.is_controlled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400">CD</span>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{med.dose} | {med.route} | {med.form}</p>
                          {/* PRN Protocol Card */}
                          {(med.prn_indication || med.prn_protocol) && (
                            <div className="mt-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3" data-testid={`prn-protocol-${med.id}`}>
                              <p className="text-[10px] font-medium text-amber-400 mb-1">PRN Protocol</p>
                              {med.prn_indication && <p className="text-xs text-foreground"><strong>Indication:</strong> {med.prn_indication}</p>}
                              {med.prn_max_dose && <p className="text-xs text-foreground"><strong>Max dose:</strong> {med.prn_max_dose}</p>}
                              {med.prn_min_interval_hours && <p className="text-xs text-foreground"><strong>Min interval:</strong> {med.prn_min_interval_hours}h between doses</p>}
                              {med.prn_protocol && <p className="text-xs text-foreground"><strong>Protocol:</strong> {med.prn_protocol}</p>}
                              {med.special_instructions && <p className="text-xs text-muted-foreground mt-1">{med.special_instructions}</p>}
                            </div>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-1">Given today: {givenToday} time(s)</p>
                        </div>
                        {canAdmin && (
                          <Button size="sm" className="btn-primary flex-shrink-0" onClick={() => openAdminDialog(med, 'PRN')} data-testid={`prn-give-${med.id}`}>
                            <Plus className="w-3 h-3 mr-1" />Give
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* RECONCILIATION TAB */}
        {tab === 'recon' && (
          <div className="space-y-3" data-testid="recon-tab">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Hospital discharge medication reconciliation</p>
              {isAdmin && <Button size="sm" className="btn-primary" onClick={() => setReconDialog(true)} data-testid="new-recon-btn"><Plus className="w-3 h-3 mr-1" />New Reconciliation</Button>}
            </div>
            {reconciliations.length === 0 ? <p className="text-center text-muted-foreground py-8">No reconciliations recorded</p> : (
              reconciliations.map(r => (
                <Card key={r.id} className="glass-card border-border">
                  <CardContent className="p-3 text-xs">
                    <div className="flex items-center justify-between"><span className="font-medium text-foreground">{r.source?.replace(/_/g, ' ')} - {r.hospital_name}</span><span className={`text-[10px] px-1.5 rounded-full ${r.status === 'completed' ? 'bg-[#A8E6CF]/10 text-[#A8E6CF]' : 'bg-amber-500/10 text-amber-400'}`}>{r.status}</span></div>
                    <p className="text-muted-foreground mt-1">Date: {r.reconciliation_date} | Discharge: {r.discharge_date} | By: {r.reconciled_by_name}</p>
                    {r.changes?.length > 0 && <p className="text-muted-foreground mt-1">{r.changes.length} change(s)</p>}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* REPORTS TAB */}
        {tab === 'reports' && (
          <div className="space-y-4" data-testid="reports-tab">
            {!reports ? <p className="text-center text-muted-foreground py-8">Loading reports...</p> : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Total Administrations" value={reports.summary?.total || 0} />
                  <MetricCard label="Given" value={reports.summary?.given || 0} color="#A8E6CF" />
                  <MetricCard label="Refused" value={reports.summary?.refused || 0} color="#F44336" alert={reports.summary?.refused > 0} />
                  <MetricCard label="Errors" value={reports.summary?.errors || 0} color="#F44336" alert={reports.summary?.errors > 0} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MetricCard label="Late" value={reports.summary?.late || 0} color="#FF9800" />
                  <MetricCard label="Early" value={reports.summary?.early || 0} color="#60A5FA" />
                  <MetricCard label="PRN Given" value={reports.summary?.prn_administered || 0} />
                  <MetricCard label="Compliance Rate" value={`${reports.summary?.compliance_rate || 0}%`} color={reports.summary?.compliance_rate >= 95 ? '#A8E6CF' : '#FF9800'} />
                </div>
                {reports.patterns?.top_refused?.length > 0 && (
                  <Card className="glass-card border-border"><CardContent className="p-3"><p className="text-xs font-medium text-foreground mb-2">Top Refused Medications</p>{reports.patterns.top_refused.map((r, i) => <p key={i} className="text-xs text-muted-foreground">{r.medication}: {r.count} refusal(s)</p>)}</CardContent></Card>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Administration Dialog */}
      <Dialog open={adminDialog} onOpenChange={setAdminDialog}>
        <DialogContent className="bg-background border-border max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Administer Medication</DialogTitle>
            <DialogDescription>Confirm administration with safety checks (CQC Reg 12)</DialogDescription>
          </DialogHeader>
          {selectedMed && (
            <div className="space-y-3">
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm font-medium text-foreground">{selectedMed.medication_name}</p>
                <p className="text-xs text-muted-foreground">{selectedMed.dose} | {selectedMed.route} | {selectedMed.form || selectedMed.frequency}</p>
                {selectedMed.special_instructions && <p className="text-xs text-amber-400 mt-1">{selectedMed.special_instructions}</p>}
              </div>

              {/* Safety alerts */}
              {safetyAlerts.length > 0 && (
                <div className="space-y-1">
                  {safetyAlerts.map((a, i) => (
                    <div key={i} className={`p-2 rounded-lg text-xs border ${a.severity === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}>
                      <strong>{a.type?.toUpperCase()}:</strong> {a.message}
                    </div>
                  ))}
                  {safetyAlerts.some(a => a.type === 'duplicate') && (
                    <label className="flex items-center gap-2 text-xs text-foreground">
                      <input type="checkbox" checked={adminForm.duplicate_override} onChange={e => setAdminForm(f => ({ ...f, duplicate_override: e.target.checked }))} /> I confirm this is intentional
                    </label>
                  )}
                  {safetyAlerts.some(a => a.type === 'allergy') && (
                    <Input value={adminForm.allergy_override_reason} onChange={e => setAdminForm(f => ({ ...f, allergy_override_reason: e.target.value }))} placeholder="Allergy override reason (required)" className="bg-muted border-red-500/30 text-foreground text-xs" />
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground">Status</label>
                  <select value={adminForm.status} onChange={e => setAdminForm(f => ({ ...f, status: e.target.value }))} className="w-full bg-muted border border-border rounded-md px-2 py-1.5 text-foreground text-xs">
                    <option value="given">Given</option><option value="refused">Refused</option><option value="withheld">Withheld</option><option value="omitted">Omitted</option><option value="self_administered">Self Administered</option><option value="error">Error</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Actual Time</label>
                  <Input type="time" value={adminForm.actual_time} onChange={e => setAdminForm(f => ({ ...f, actual_time: e.target.value }))} className="bg-muted border-border text-foreground text-xs" />
                </div>
              </div>

              {adminForm.status !== 'given' && (
                <Input value={adminForm.reason_not_given} onChange={e => setAdminForm(f => ({ ...f, reason_not_given: e.target.value }))} placeholder="Reason not given (required)" className="bg-muted border-border text-foreground text-xs" />
              )}
              {selectedMed.is_prn && adminForm.status === 'given' && (
                <Input value={adminForm.prn_reason} onChange={e => setAdminForm(f => ({ ...f, prn_reason: e.target.value }))} placeholder="PRN reason" className="bg-muted border-border text-foreground text-xs" />
              )}
              {selectedMed.is_controlled && (
                <Input value={adminForm.witnessed_by_name} onChange={e => setAdminForm(f => ({ ...f, witnessed_by_name: e.target.value }))} placeholder="Witness name (required for CD)" className="bg-muted border-border text-foreground text-xs" />
              )}
              {selectedMed.is_patch && (
                <Input value={adminForm.patch_site} onChange={e => setAdminForm(f => ({ ...f, patch_site: e.target.value }))} placeholder="Patch application site" className="bg-muted border-border text-foreground text-xs" />
              )}
              {selectedMed.is_insulin && (
                <div className="grid grid-cols-2 gap-2">
                  <Input value={adminForm.insulin_units} onChange={e => setAdminForm(f => ({ ...f, insulin_units: e.target.value }))} placeholder="Units given" className="bg-muted border-border text-foreground text-xs" type="number" />
                  <Input value={adminForm.blood_glucose_before} onChange={e => setAdminForm(f => ({ ...f, blood_glucose_before: e.target.value }))} placeholder="BG before (mmol/L)" className="bg-muted border-border text-foreground text-xs" type="number" step="0.1" />
                </div>
              )}
              <textarea value={adminForm.notes} onChange={e => setAdminForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" className="w-full bg-muted border border-border rounded-md px-2 py-1.5 text-foreground text-xs min-h-[40px]" />
              <Button className="w-full btn-primary" onClick={submitAdmin} data-testid="confirm-admin-btn">Confirm Administration</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={reconDialog} onOpenChange={setReconDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader><DialogTitle>Medication Reconciliation</DialogTitle><DialogDescription>Record hospital discharge medication changes (CQC Reg 12)</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={reconForm.hospital_name} onChange={e => setReconForm(f => ({ ...f, hospital_name: e.target.value }))} placeholder="Hospital name" className="bg-muted border-border text-foreground" />
            <Input type="date" value={reconForm.discharge_date} onChange={e => setReconForm(f => ({ ...f, discharge_date: e.target.value }))} className="bg-muted border-border text-foreground" />
            <textarea value={reconForm.changes} onChange={e => setReconForm(f => ({ ...f, changes: e.target.value }))} placeholder="Changes (one per line)&#10;e.g. Amlodipine 5mg STOPPED&#10;Ramipril 2.5mg ADDED" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[80px]" />
            <textarea value={reconForm.notes} onChange={e => setReconForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[40px]" />
            <Button className="w-full btn-primary" onClick={saveRecon} data-testid="save-recon-btn">Save Reconciliation</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function MetricCard({ label, value, color, alert }) {
  return (
    <Card className={`glass-card ${alert ? 'border-red-500/30' : 'border-border'}`}>
      <CardContent className="p-3 text-center">
        <p className="text-xl font-bold" style={{ color: color || 'inherit' }}>{value}</p>
        <p className="text-[10px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
